import { useEffect } from 'react';
import { useRouter } from 'next/router';
import useIsEditing from 'lib/hooks/useIsEditing';

/**
 * Place any global, page-level DOM manipulation here.
 * This component renders nothing and runs after hydration on every page.
 */
const CustomDomScripts = (): JSX.Element | null => {
  const router = useRouter();
  const isEditing = useIsEditing();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isEditing) return;

    const runScripts = () => {
      const sidenavSections = Array.from(
        document.querySelectorAll('.sidenav-section-component')
      ) as HTMLElement[];
      const hasProviderListing = Array.from(
        document.querySelectorAll('.sidenav-section-component.provider-listing-section')
      ) as HTMLElement[];
      sidenavSections.forEach((section) => {
        const subHeading = section
          .closest('.component')
          ?.querySelector('.sidenav-section-subheading');

        const hasResultSection = section?.querySelector('[data-hasresult]');
        const hasProviderSection = section.classList?.contains('provider-listing-section') || false;

        const accordionItemId = section.dataset.accordionitemid;
        const accordionItem = document.querySelector(`[data-accordionItemId="${accordionItemId}"]`);
        if (
          (section.children.length === 0 || hasProviderSection) &&
          subHeading === null &&
          (hasProviderListing.length === 0 ||
            hasResultSection?.getAttribute('data-hasresult') === 'false')
        ) {
          if (accordionItem) {
            accordionItem.closest('li')?.classList.add('hidden');
          }
          section.closest('.component')?.classList.add('hidden');
        } else {
          if (hasResultSection?.getAttribute('data-hasresult') === 'true') {
            accordionItem?.closest('li')?.classList.remove('hidden');
            section.closest('.component')?.classList.remove('hidden');
          }
        }
      });
    };

    const runWhenDomReady = () => {
      if (document.readyState === 'complete' || document.readyState === 'interactive') {
        // Ensure we run after React has painted
        requestAnimationFrame(() => runScripts());
      } else {
        window.addEventListener('DOMContentLoaded', runScripts, { once: true });
      }
    };

    // Initial run on first load
    runWhenDomReady();

    // Re-run after route changes (client-side navigation)
    const onRouteChangeComplete = () => runWhenDomReady();
    router.events.on('routeChangeComplete', onRouteChangeComplete);

    // Observe DOM mutations that might add/remove target elements
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.addedNodes && mutation.addedNodes.length > 0) {
          // Debounce by next frame to allow layout/paint
          requestAnimationFrame(() => runScripts());
          break;
        }
      }
    });
    try {
      observer.observe(document.body, { childList: true, subtree: true });
    } catch {
      // no-op
    }

    return () => {
      router.events.off('routeChangeComplete', onRouteChangeComplete);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.events]);

  return null;
};

export default CustomDomScripts;
